import React, { useEffect, useState, useMemo, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { radialDimensionDetails } from "../../../services/api-services";
import { toastEmitter } from "../../../utills/utilities";
import { API_RESPONSE } from "../../../utills/app-constant";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import DownloadGraphButton from "../../../user/pages/page/DownloadGraphButton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const RadialDimensionHorizontal = ({ selectedCompanyId, graphName, myhandleExport }) => {
  const [rowData, setRowData] = useState(null);
  const [remark, setRemark] = useState("");
  const [isAttempt, setIsAttempt] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalImportance, setTotalImportance] = useState(0);
  const [totalCurrent, setTotalCurrent] = useState(0);
  const [totalFuture, setTotalFuture] = useState(0);

  const chartOptions = useMemo(() => ({
    chart: { id: "dimension_details", type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "70%",
        dataLabels: { position: "outside" },
        borderRadius: 0,
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "12px", colors: ["#000"] },
      textAnchor: "start",
      formatter: (val) => val.toFixed(2),
    },
    colors: ['#557D39', '#F7BB1C', '#D42327'],
    xaxis: { categories, labels: { formatter: (value) => value?.toFixed(0) }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { show: true }, axisBorder: { show: false }, axisTicks: { show: false } },
    grid: { show: false },
    legend: { position: "top", horizontalAlign: "center" },
    stroke: { show: false },
  }), [categories]);

  const getRadialDimensionDetailsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await radialDimensionDetails(selectedCompanyId, graphName);
      setLoading(false);

      if (response?.data?.responseCode === 200) {
        const dimensions = response?.data?.responseBody || [];
        setCategories(dimensions.map((item) => item.dimensionName));
        setSeries([
          { name: "Current Score", data: dimensions.map((item) => item.currentAverageValue) },
          { name: "Future Score", data: dimensions.map((item) => item.futureAverageValue) },
          { name: "Importance Score", data: dimensions.map((item) => item.importantAverageValue) },
        ]);
        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt ?? 0);

        setTotalImportance(response?.data?.totalImportanceAverage || 0);
        setTotalCurrent(response?.data?.totalCurrentAverage || 0);
        setTotalFuture(response?.data?.totalFutureAverage || 0);
      } else {
        toastEmitter(response?.data?.responseCode === 404 ? "info" : "error", response?.data?.message || "No data found.");
      }
    } catch (err) {
      setLoading(false);
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    }
  }, [selectedCompanyId, graphName]);

  useEffect(() => {
    if (selectedCompanyId) getRadialDimensionDetailsData();
  }, [selectedCompanyId, getRadialDimensionDetailsData]);

  useEffect(() => {
    if (series.some(s => s.data.some(val => val > 0))) {
      setTimeout(() => generateChartImage("dimension_details", "dimension_details", rowData?.remark), 300);
    }
  }, [series, rowData]);

  const exportSummaryToExcel = (totalCurrent, totalFuture, totalImportance, fileName = "Radial Dimension details") => {
    const heading = ["Radial Dimension details"];
    const summaryRows = [
      heading,
      [], // empty row for spacing (optional)
      [`Total Current = ${totalCurrent.toFixed(2)}`],
      [`Total Future = ${totalFuture.toFixed(2)}`],
      [`Total Importance = ${totalImportance.toFixed(2)}`],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(summaryRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "summaryRows");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  return (
    <div id="chart">
      {loading ? (
        <div className="loader-container"><div className="loader"></div></div>
      ) : (
        <>
          <div className="d-flex justify-content-between">
            <h5>Dimension</h5>
            <div className="d-flex">
              <button className="mb-3 dbtn px-3 py-2 text-white bg-primary rounded border-0 cursor-pointer me-3" onClick={() => exportSummaryToExcel(totalCurrent, totalFuture, totalImportance)}>Download Excel</button>
              <DownloadGraphButton chartID="chart4" myhandleExport={myhandleExport} />
            </div>
          </div>

          <div className="chart4" data-heading=" Dimension details" data-remark={remark}>
            <ReactApexChart options={chartOptions} series={series} type="bar" />
          </div>

        </>
      )}

      <div style={{ marginTop: "158px" }}>
        <div className="d-flex justify-content-around rounded my-3 ">
          <div className="text-center ">
            <h6 className="textfont12">Total Current</h6>
            <h5 className="text-success textfont13">{totalCurrent?.toFixed(2)}</h5>
          </div>
          <div className="text-center ">
            <h6 className="textfont12">Total Future</h6>
            <h5 className="text-warning textfont13">{totalFuture?.toFixed(2)}</h5>
          </div>
          <div className="text-center">
            <h6 className="textfont12">Total Importance</h6>
            <h5 className="text-danger textfont13">{totalImportance?.toFixed(2)}</h5>
          </div>
        </div>
        <Remark graphName="RadialDimensionhorizontaldetails" selectedCompanyId={selectedCompanyId} initialRemark={remark} remarkId={remarkId} isAttempt={isAttempt} setRemarkprops={setRemark} />
      </div>
    </div>
  );
};

export default RadialDimensionHorizontal;
